import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

function SelectorIdentificationTool({ url, config, onChange, onTest, isTesting: parentIsTesting, testResults: parentTestResults, openPreview: parentOpenPreview = false }) {
    // Initialize selectors with page type information
    const initializeSelectors = (configSelectors = {}) => {
        const initialized = {};
        Object.entries(configSelectors).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null && value.selector) {
                // Value is already in the new format
                // Ensure selector is a string
                const selectorValue = (typeof value.selector === 'string' || typeof value.selector === 'number')
                    ? String(value.selector)
                    : '';
                initialized[key] = {
                    selector: selectorValue,
                    pageType: value.pageType || 'list'
                };
            } else {
                // Convert old format to new format (default to list page)
                // Ensure value is a string
                const selectorValue = (typeof value === 'string' || typeof value === 'number')
                    ? String(value)
                    : '';
                initialized[key] = {
                    selector: selectorValue,
                    pageType: 'list' // Default to list page
                };
            }
        });
        return initialized;
    };

    const [selectors, setSelectors] = useState(initializeSelectors(config?.selectors || {}));
    const [previewUrl, setPreviewUrl] = useState('');
    const [iframeKey, setIframeKey] = useState(0);
    const [openPreview, setOpenPreview] = useState(false);

    // Common e-commerce elements
    const commonElements = [
        { id: 'product_list', label: 'Product List', description: 'Container for product listings' },
        { id: 'product_container', label: 'Product Container', description: 'Individual product container' },
        { id: 'product_name', label: 'Product Name', description: 'Name/title of the product' },
        { id: 'price_per_piece', label: 'Price per Piece', description: 'Price for individual items' },
        { id: 'stock_info', label: 'Stock Information', description: 'Availability/stock status' },
        { id: 'manufacturer', label: 'Manufacturer', description: 'Product manufacturer/brand' },
        { id: 'product_number', label: 'Product Number', description: 'SKU or product identifier' },
        { id: 'unique_id', label: 'Unique ID', description: 'Unique identifier for the product' },
        { id: 'product_image', label: 'Product Image', description: 'Main product image' },
        { id: 'product_description', label: 'Product Description', description: 'Detailed product description' },
        { id: 'category', label: 'Category', description: 'Product category' },
        { id: 'next_button', label: 'Next Page Button', description: 'Button to navigate to next page' }
    ];

    // Update selectors when config changes
    useEffect(() => {
        if (config?.selectors) {
            setSelectors(initializeSelectors(config.selectors));
        }
    }, [config]);

    // Update openPreview when parentTestResults changes from null/undefined to a value
    useEffect(() => {
        if (parentTestResults) {
            setOpenPreview(true);
        }
    }, [parentTestResults]);

    // Handle selector change
    const handleSelectorChange = (elementId, value) => {
        // Ensure value is a string (in case of empty input or object)
        const selectorValue = (typeof value === 'string' || typeof value === 'number') ? String(value) : '';
        const currentSelector = selectors[elementId] || { selector: '', pageType: 'list' };
        const newSelectors = {
            ...selectors,
            [elementId]: {
                ...currentSelector,
                selector: selectorValue
            }
        };

        // Debugging: log incoming value/type and preview of the change to catch non-string slips
        console.debug('[SelectorIdentificationTool] handleSelectorChange', {
            elementId,
            incomingType: typeof value,
            incomingValue: value,
            selectorValue,
            currentSelector,
            newSelectorsPreview: newSelectors[elementId],
            selectorsBefore: selectors
        });

        // Deep clone to avoid accidental object references stored in higher-level state
        let clonedSelectors;
        try {
            clonedSelectors = JSON.parse(JSON.stringify(newSelectors));
        } catch (err) {
            // Fallback: coerce entries manually
            clonedSelectors = Object.keys(newSelectors).reduce((acc, k) => {
                const s = newSelectors[k];
                acc[k] = {
                    selector: (typeof s?.selector === 'string' || typeof s?.selector === 'number') ? String(s.selector) : '',
                    pageType: s?.pageType || 'list'
                };
                return acc;
            }, {});
        }

        setSelectors(clonedSelectors);
        if (onChange) {
            onChange({ ...config, selectors: clonedSelectors });
        }
    };

    // Handle selector change for TextField - ensure proper string handling
    const handleSelectorChangeTextField = (elementId, e) => {
        const value = e.target.value;
        handleSelectorChange(elementId, value);
    };

    // Handle page type change
    const handlePageTypeChange = (elementId, pageType) => {
        const currentSelector = selectors[elementId] || { selector: '', pageType: 'list' };
        // Ensure selector is still a string
        const selectorValue = (typeof currentSelector.selector === 'string' || typeof currentSelector.selector === 'number')
            ? String(currentSelector.selector)
            : '';
        const newSelectors = {
            ...selectors,
            [elementId]: {
                ...currentSelector,
                pageType: pageType,
                selector: selectorValue
            }
        };
        setSelectors(newSelectors);
        if (onChange) {
            onChange({ ...config, selectors: newSelectors });
        }
    };

    // Add custom element - This function should not be called since the button was removed
    const handleAddCustomElement = () => {
        const customId = `custom_${Date.now()}`;
        const newSelectors = {
            ...selectors,
            [customId]: { selector: '', pageType: 'list' }
        };
        setSelectors(newSelectors);
        if (onChange) {
            onChange({ ...config, selectors: newSelectors });
        }
    };

    // Remove custom element - This function should not be called since the button was removed
    const handleRemoveCustomElement = (elementId) => {
        const newSelectors = { ...selectors };
        delete newSelectors[elementId];
        setSelectors(newSelectors);
        if (onChange) {
            onChange({ ...config, selectors: newSelectors });
        }
    };





    // Handle preview URL change
    const handlePreviewUrlChange = () => {
        setIframeKey(prev => prev + 1);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                Selector Identification Tool
            </Typography>

            <Grid container spacing={3}>
                {commonElements.map((element) => (
                    <Grid item xs={12} key={element.id}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        {element.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {element.description}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        value={selectors[element.id]?.selector || ''}
                                        onChange={(e) => handleSelectorChangeTextField(element.id, e)}
                                        placeholder={`Enter CSS selector for ${element.label}`}
                                        variant="contained"
                                        sx={{ mb: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectors[element.id]?.pageType === 'detail'}
                                                onChange={(e) => handlePageTypeChange(element.id, e.target.checked ? 'detail' : 'list')}
                                            />
                                        }
                                        label="Detail Page"
                                        sx={{ mb: 0, alignSelf: 'center' }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                {/* Custom elements */}
                {Object.keys(selectors).filter(key =>
                    !commonElements.some(el => el.id === key)
                ).map((customKey) => (
                    <Grid item xs={12} key={customKey}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        Custom Element
                                    </Typography>
                                    <IconButton
                                        onClick={() => handleRemoveCustomElement(customKey)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        value={selectors[customKey]?.selector || ''}
                                        onChange={(e) => handleSelectorChangeTextField(customKey, e)}
                                        placeholder="Enter CSS selector"
                                        variant="contained"
                                        sx={{ mb: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectors[customKey]?.pageType === 'detail'}
                                                onChange={(e) => handlePageTypeChange(customKey, e.target.checked ? 'detail' : 'list')}
                                            />
                                        }
                                        label="Detail Page"
                                        sx={{ mb: 0, alignSelf: 'center' }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                {/* Add Custom Element button has been removed as per requirements */}
            </Grid>

            {/* Preview Dialog */}
            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle>Scraper Preview</DialogTitle>
                <DialogContent>
                    {parentTestResults ? (
                        <Box>
                            {parentTestResults.success ? (
                                <Box>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {parentTestResults.message}
                                    </Alert>
                                    <Typography variant="h6" gutterBottom>
                                        Found {parentTestResults.products_found || parentTestResults.products?.length || 0} products
                                    </Typography>
                                    {parentTestResults.sample_products && parentTestResults.sample_products.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Sample Products:
                                            </Typography>
                                            {parentTestResults.sample_products.map((product, index) => (
                                                <Card key={index} sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Typography><strong>Name:</strong> {product.product_name}</Typography>
                                                        <Typography><strong>Price:</strong> {product.price_per_piece}</Typography>
                                                        <Typography><strong>Stock:</strong> {product.stock_info}</Typography>
                                                        <Typography><strong>Manufacturer:</strong> {product.manufacturer}</Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                    {parentTestResults.products && parentTestResults.products.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Sample Products (from test):
                                            </Typography>
                                            {parentTestResults.products.map((product, index) => (
                                                <Card key={index} sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Typography><strong>Name:</strong> {product.name || 'N/A'}</Typography>
                                                        <Typography><strong>Price:</strong> {product.price || 'N/A'}</Typography>
                                                        <Typography><strong>Link:</strong> {product.link || 'N/A'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Alert severity="error">
                                    {parentTestResults.message}
                                </Alert>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={() => setOpenPreview(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Preview URL Dialog */}
            <Dialog open={false} onClose={() => { }} maxWidth="lg" fullWidth>
                <DialogTitle>Page Preview</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Preview URL"
                            value={previewUrl}
                            onChange={(e) => setPreviewUrl(e.target.value)}
                            placeholder="Enter URL to preview"
                        />
                        <Button
                            onClick={handlePreviewUrlChange}
                            variant="contained"
                            sx={{ mt: 1 }}
                        >
                            Load Preview
                        </Button>
                    </Box>
                    <iframe
                        key={iframeKey}
                        src={previewUrl || url}
                        title="Page Preview"
                        width="100%"
                        height="600px"
                        style={{ border: '1px solid #ccc' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={() => { }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SelectorIdentificationTool;